// Müşterinin alan adındaki (örn. tahsilat.firma.com.tr) tahsilat giriş ekranının saf yardımcıları.
// `node --test` ile doğrudan test edilir (tests/customDomainLogin.spec.mjs).
//
// Akış: giriş ekranı Platform kimlik doğrulama uçlarını AYNI ORIGIN'deki `/platform-api/` öneki üzerinden
// çağırır (host nginx bu öneki Platform API'ye iletir; CORS ve çerez ayarı gerekmez). Platform, isteğin
// geldiği alan adını aktif özel alan adı olarak firmaya çözer ve girişi o firmaya kilitler. Oturum
// tamamlanınca yanıttaki e-Kolay Tahsilat takas (exchange) token'ı mevcut `platform-login` ucuna verilir;
// portal (master) oturumu alan adında tutulmaz, hemen kapatılır.

export const CUSTOM_DOMAIN_LOGIN_PATH = "/giris";
export const PLATFORM_AUTH_PREFIX = "/platform-api/api/auth";
export const EPAYMENT_PRODUCT_NAME = "e-Kolay Tahsilat";

export const LOGIN_STATUS = Object.freeze({
  authenticated: "authenticated",
  selectionRequired: "selection_required",
  otpRequired: "otp_required",
  passwordChangeRequired: "password_change_required",
});

const DEFAULT_TIMEOUT_MS = 30000;
const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Portal giriş ekranındaki firma banner yuvaları (Platform `auth/company-login-banner/{companyId}/{slot}`). */
export const LOGIN_BANNER_SLOTS = Object.freeze([1, 2, 3]);

/**
 * Firmanın portal giriş ekranında gösterilen banner adresleri. Banner'lar Platform API'den anonim servis edilir;
 * portal da aynı uçları kullanır. Platform API adresi https değilse ya da firma kimliği GUID değilse boş liste.
 * @param {string | null | undefined} platformApiUrl örn. "https://api.finmony.com/api"
 * @param {string | null | undefined} companyId
 * @returns {string[]}
 */
export const buildLoginBannerUrls = (platformApiUrl, companyId) => {
  const base = String(platformApiUrl ?? "").trim().replace(/\/+$/, "");
  const id = String(companyId ?? "").trim();
  if (!/^https:\/\/[^\s/?#]+/i.test(base) || !GUID_PATTERN.test(id)) return [];
  return LOGIN_BANNER_SLOTS.map((slot) => `${base}/auth/company-login-banner/${id.toLowerCase()}/${slot}`);
};

/**
 * camelCase ya da PascalCase alanı okur (Platform camelCase, EPayment PascalCase döner).
 * @param {any} source
 * @param {string} camelName
 */
export const pick = (source, camelName) => {
  if (!source || typeof source !== "object") return undefined;
  const pascalName = camelName.charAt(0).toUpperCase() + camelName.slice(1);
  return source[camelName] ?? source[pascalName];
};

/**
 * @param {unknown} value
 * @returns {string | null}
 */
const nonEmptyString = (value) => (typeof value === "string" && value.trim() ? value.trim() : null);

/**
 * Platform kimlik doğrulama ucunun aynı origin adresi.
 * @param {string} origin
 * @param {string} path örn. "login", "login/otp/verify"
 */
export const buildPlatformAuthUrl = (origin, path) =>
  `${String(origin ?? "").trim().replace(/\/+$/, "")}${PLATFORM_AUTH_PREFIX}/${String(path ?? "").replace(/^\/+/, "")}`;

/**
 * Platform `LoginFlowResponseDto` yanıtını giriş ekranının kullandığı biçime çevirir.
 * @param {any} raw
 */
export const normalizeLoginFlowResponse = (raw) => {
  const status = nonEmptyString(pick(raw, "status")) ?? "";
  const selection = pick(raw, "selection");
  const otp = pick(raw, "otp");
  const passwordChange = pick(raw, "passwordChange");
  const challengeId =
    nonEmptyString(pick(selection, "challengeId")) ??
    nonEmptyString(pick(otp, "challengeId")) ??
    nonEmptyString(pick(passwordChange, "challengeId"));

  const contexts = (Array.isArray(pick(selection, "contexts")) ? pick(selection, "contexts") : [])
    .map((entry) => ({
      companyId: nonEmptyString(String(pick(entry, "companyId") ?? "")),
      companyName: nonEmptyString(pick(entry, "companyName")) ?? "",
      dealerId: nonEmptyString(String(pick(entry, "dealerId") ?? "")),
      dealerCode: nonEmptyString(pick(entry, "dealerCode")),
      dealerTitle: nonEmptyString(pick(entry, "dealerTitle")),
      parentDealerTitle: nonEmptyString(pick(entry, "parentDealerTitle")),
      isSubDealer: Boolean(pick(entry, "isSubDealer")),
      isDefault: Boolean(pick(entry, "isDefault")),
      isPrimaryCompany: Boolean(pick(entry, "isPrimaryCompany")),
    }))
    .filter((entry) => entry.companyId);

  return {
    status,
    challengeId,
    session: pick(raw, "session") ?? null,
    otp: otp
      ? {
          phoneRequired: Boolean(pick(otp, "phoneRequired")),
          maskedPhone: nonEmptyString(pick(otp, "maskedPhone")),
          resendAfterSeconds: Math.max(0, Number(pick(otp, "resendAfterSeconds") ?? 0) || 0),
          otpExpiresAt: parseTimestamp(pick(otp, "otpExpiresAt")),
        }
      : null,
    contexts,
  };
};

/**
 * Tarih alanını epoch milisaniyeye çevirir (portal da `new Date(...)` ile okur); geçersizse null.
 * @param {unknown} value
 * @returns {number | null}
 */
const parseTimestamp = (value) => {
  if (typeof value !== "string" || !value.trim()) return null;
  const time = Date.parse(value);
  return Number.isFinite(time) ? time : null;
};

/**
 * `login/otp/resend` ucu sarmalanmamış `PendingLoginOtpDto` döner.
 * @param {any} raw
 * @returns {{ challengeId: string | null, phoneRequired: boolean, maskedPhone: string | null,
 *   resendAfterSeconds: number, otpExpiresAt: number | null } | null}
 */
export const normalizePendingOtp = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  return {
    challengeId: nonEmptyString(pick(raw, "challengeId")),
    phoneRequired: Boolean(pick(raw, "phoneRequired")),
    maskedPhone: nonEmptyString(pick(raw, "maskedPhone")),
    resendAfterSeconds: Math.max(0, Number(pick(raw, "resendAfterSeconds") ?? 0) || 0),
    otpExpiresAt: parseTimestamp(pick(raw, "otpExpiresAt")),
  };
};

/**
 * Oturum yanıtındaki e-Kolay Tahsilat takas token'ını döndürür; yoksa null.
 * @param {any} session
 * @returns {string | null}
 */
export const pickEPaymentExchangeToken = (session) => {
  const tokens = pick(session, "productTokens");
  if (!Array.isArray(tokens)) return null;
  const entry = tokens.find((item) => nonEmptyString(pick(item, "productName")) === EPAYMENT_PRODUCT_NAME);
  if (!entry) return null;
  const token = pick(entry, "token");
  return typeof token === "string" ? nonEmptyString(token) : nonEmptyString(pick(token, "accessToken"));
};

/**
 * Tahsilat token'ı üretilemediyse (ürün token hatası) true.
 * @param {any} session
 */
export const hasEPaymentTokenFailure = (session) => {
  const failures = pick(session, "productTokenFailures");
  return Array.isArray(failures)
    && failures.some((item) => nonEmptyString(pick(item, "productName")) === EPAYMENT_PRODUCT_NAME);
};

/**
 * Portal (master) oturumunu kapatmak için gereken token'lar.
 * @param {any} session
 * @returns {{ accessToken: string | null, refreshToken: string | null }}
 */
export const pickMasterTokens = (session) => {
  const master = pick(session, "masterToken");
  return {
    accessToken: nonEmptyString(pick(master, "accessToken")),
    refreshToken: nonEmptyString(pick(master, "refreshToken")),
  };
};

/**
 * Platform hata yanıtından (ProblemDetails / ValidationProblemDetails) kullanıcıya gösterilecek Türkçe mesaj.
 * @param {number} status
 * @param {any} body
 */
export const describeAuthError = (status, body) => {
  if (status === 429) return "Çok fazla deneme yapıldı. Lütfen bir dakika sonra tekrar deneyin.";

  const errors = pick(body, "errors");
  if (errors && typeof errors === "object") {
    const first = Object.values(errors).flat().find((message) => nonEmptyString(message));
    if (first) return String(first).trim();
  }

  const detail = nonEmptyString(pick(body, "detail")) ?? nonEmptyString(pick(body, "message"));
  if (detail) return detail;

  if (status === 401) return "Kullanıcı adı veya şifre hatalı.";
  if (status === 423) return "Hesabınız geçici olarak kilitlendi. Lütfen daha sonra tekrar deneyin.";
  if (status === 0) return "Sunucuya ulaşılamadı. İnternet bağlantınızı kontrol edip tekrar deneyin.";
  return "Giriş sırasında bir hata oluştu. Lütfen tekrar deneyin.";
};

/**
 * Platform kimlik doğrulama ucunu aynı origin üzerinden çağırır. Hata fırlatmaz.
 * @param {{ origin: string, path: string, body?: any, accessToken?: string | null,
 *   fetchImpl?: (input: string, init?: any) => Promise<any>, timeoutMs?: number }} options
 * @returns {Promise<{ ok: boolean, status: number, data: any }>}
 */
export const callPlatformAuth = async ({ origin, path, body, accessToken, fetchImpl, timeoutMs }) => {
  const doFetch = fetchImpl ?? (typeof fetch === "function" ? fetch : null);
  if (!doFetch) return { ok: false, status: 0, data: null };

  const controller = typeof AbortController === "function" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs ?? DEFAULT_TIMEOUT_MS) : null;
  const headers = { Accept: "application/json", "Content-Type": "application/json" };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  try {
    const response = await doFetch(buildPlatformAuthUrl(origin, path), {
      method: "POST",
      headers,
      body: JSON.stringify(body ?? {}),
      // Portal çerezi bu adreste ne yazılır ne okunur.
      credentials: "omit",
      cache: "no-store",
      signal: controller?.signal,
    });

    let data = null;
    try {
      const text = await response.text();
      data = text ? JSON.parse(text) : null;
    } catch {
      data = null;
    }

    return { ok: Boolean(response.ok), status: Number(response.status) || 0, data };
  } catch {
    return { ok: false, status: 0, data: null };
  } finally {
    if (timer) clearTimeout(timer);
  }
};
